import { useTranslation } from "react-i18next";
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ShoppingCart } from "lucide-react";
import { CartItemList } from "@/components/orders/CartItemList";
import { RecommendationChips } from "@/components/orders/RecommendationChips";
import { Product, ProductSelection } from "@/types/product";
import { useProductSummary } from "@/hooks/use-product-summary";
import { useCartCountLabel } from "@/components/orders/use-cart-count-label";
import { formatCentsAsDollars } from "@/lib/price-utils";

interface CartPanelProps {
  selectedProducts: ProductSelection[];
  products?: Product[];
  productsLoading?: boolean;
  onProductQuantityChange: (productId: string, quantity: number) => void;
  onProductRemove: (productId: string) => void;
  onClearAll: () => void;
  onAddProduct?: (product: Product, quantity: number) => void;
  onCheckout: () => void;
  showPricing?: boolean;
  disabled?: boolean;
}

export const CartPanel: React.FC<CartPanelProps> = ({
  selectedProducts,
  products,
  productsLoading,
  onProductQuantityChange,
  onProductRemove,
  onClearAll,
  onAddProduct,
  onCheckout,
  showPricing = false,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const itemCount = selectedProducts.length;

  const summary = useProductSummary({
    selectedProducts: selectedProducts.map((p) => ({
      quantity: p.quantity,
      unitPrice: "unitPrice" in p ? p.unitPrice : undefined,
    })),
    showPricing,
  });
  const countLabel = useCartCountLabel(itemCount, summary.totalItems);

  return (
    <Card aria-label={t("components.orders.cartPanel.shoppingCart")} className="flex flex-col flex-1 min-h-0">
      <CardHeader className="pb-3 flex-shrink-0">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <ShoppingCart className="h-4 w-4" aria-hidden="true" />
            {t("common.cart")}
            {itemCount > 0 && (
              <span className="text-sm font-normal text-muted-foreground">
                ({countLabel})
              </span>
            )}
          </CardTitle>
          {showPricing && summary.totalValueCents > 0 && (
            <span className="text-sm font-semibold">
              ${formatCentsAsDollars(summary.totalValueCents)}
              {summary.hasMissingPrices ? "+" : ""}
            </span>
          )}
        </div>
      </CardHeader>

      {/* Cart content with separate scroll zones */}
      <CardContent className="flex-1 min-h-0 flex flex-col gap-3 overflow-hidden">
        {itemCount === 0 ? (
          <div className="flex flex-col items-center justify-center py-6 text-center text-muted-foreground">
            <ShoppingCart className="h-8 w-8 mb-2 opacity-50" aria-hidden="true" />
            <p className="text-sm font-medium">{t("components.orders.cartPanel.yourCartIsEmpty")}</p>
            <p className="text-xs mt-1">{t("components.orders.cartPanel.addProductsFromThe")}</p>
          </div>
        ) : (
          <>
            <div className="flex-1 min-h-0 overflow-y-auto">
              <CartItemList
                selectedProducts={selectedProducts}
                onQuantityChange={onProductQuantityChange}
                onRemove={onProductRemove}
                onClearAll={onClearAll}
                showPricing={showPricing}
                products={products}
                productsLoading={productsLoading}
              />
            </div>
            {onAddProduct && (
              <div className="flex-shrink-0 border-t pt-3">
                <RecommendationChips
                  cartProductIds={selectedProducts.map((p) => p.productId)}
                  onAddProduct={onAddProduct}
                />
              </div>
            )}
          </>
        )}
      </CardContent>

      {/* Pinned footer */}
      <div className="flex-shrink-0 p-4 pt-3 border-t space-y-2">
        <Button
          onClick={onCheckout}
          disabled={itemCount === 0 || disabled}
          className="w-full"
        >
          {t("components.orders.cartPanel.checkOutRarr")}
        </Button>
        {itemCount > 0 && (
          <button
            onClick={() => setShowClearConfirm(true)}
            className="w-full text-xs text-muted-foreground hover:text-foreground transition-colors py-1 mt-3"
          >
            {t("components.orders.cartPanel.clearCart")}
          </button>
        )}
      </div>

      {/* Clear cart confirmation dialog */}
      <AlertDialog open={showClearConfirm} onOpenChange={setShowClearConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("components.orders.cartPanel.clearAllItems")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("components.orders.cartPanel.thisWillRemoveAll")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("components.orders.cartPanel.keepItems")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                onClearAll();
                setShowClearConfirm(false);
              }}
              className="bg-muted text-muted-foreground hover:bg-muted/80"
            >
              {t("components.orders.cartPanel.yesClearCart")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};
